import { FunctionComponent, useState, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import {
  useForm,
  withStyles,
  Skeleton,
  PackageDetails as PackageDetailsType,
  Tooltip,
  Icon,
  BodyText,
  countryToISO,
} from 'vopay';

import Header from '../Header/Header';
import StepIndicator from '../StepIndicator/StepIndicator';
import { DetailsCard, Title, Button } from 'UI';
import useFetchBillingPackages from '@/hooks/useApi/useFetchBillingPackages';
import useFetchBillingPackageDetails from '@/hooks/useApi/useFetchBillingPackageDetails';
import { useAlertDialog } from '@/providers/AlertDialog.provider';
import useBillingFields, { BillingFormValues } from './useBillingFields';
import PackageDetails, { TabValuesMap } from './PackageDetails/PackageDetails';
import agreementTooltipImg from './agreementTooltip.png';

import styles from './Billing.module.scss';

const cn = withStyles(styles);

export type BillingData = {
  package: string;
  packageDetails: TabValuesMap;
  billingPackageInfo: PackageDetailsType | null;
};

type BillingProps = {
  steps: string[];
  currentStep: number;
  country: string;
  nextStep: () => void;
  prevStep: () => void;
  onValuesChange: (values: BillingData) => void;
};

const Billing: FunctionComponent<BillingProps> = ({
  steps,
  currentStep,
  country,
  nextStep,
  prevStep,
  onValuesChange,
}) => {
  const { t } = useTranslation('translations', { keyPrefix: 'R1AccountCreation.Billing' });
  const alertDialog = useAlertDialog();

  const [isPackagesLoading, setIsPackagesLoading] = useState(true);
  const [isDetailsLoading, setIsDetailsLoading] = useState(false);
  const [isPackageDetailsValid, setIsPackageDetailsValid] = useState(false);
  const [reviewRemaining, setReviewRemaining] = useState<string[]>([]);
  const [errorSections, setErrorSections] = useState<string[]>([]);

  const [{ billingPackages, dropDownOptions }, fetchPackages] = useFetchBillingPackages({});
  const [, , fetchDetails] = useFetchBillingPackageDetails({});

  type Details = Awaited<ReturnType<typeof fetchDetails>>['PackageDetails'];
  const [details, setDetails] = useState<Details | null>(null);

  // Fetch packages (filtered by the account's country from HubSpot); refetch when it resolves
  useEffect(() => {
    fetchPackages({ country: countryToISO(country) });
  }, [country]);

  // Detect when packages have loaded (billingPackages reference changes after fetch)
  const isInitialPackagesMount = useRef(true);
  useEffect(() => {
    if (isInitialPackagesMount.current) {
      isInitialPackagesMount.current = false;
      return;
    }
    setIsPackagesLoading(false);
  }, [billingPackages]);

  const packageOptions = Object.entries(dropDownOptions).map(([id, label]) => ({ id, label }));

  // Stable wrapper passed to fields — actual handler is defined after useForm
  const onPackageChangeRef = useRef<(packageId: string) => void>(() => {});

  const latestPackageRequestRef = useRef(0);

  const fields = useBillingFields(
    packageOptions,
    (packageId) => onPackageChangeRef.current(packageId),
    isPackagesLoading,
    isDetailsLoading,
  );

  const { ctx, renderFormElements } = useForm<BillingFormValues>({
    config: fields,
    initialValues: { package: '' },
    onSubmit: () => {
      nextStep();
    },
  });

  const showError = async (message: string) => {
    await alertDialog.error(t('FetchError.title', { defaultValue: 'Failed to Fetch Package Details' }), message, {
      confirmLabel: t('FetchError.ok', { defaultValue: 'OK' }),
    });
    ctx.setFieldValue('package', '');
    setDetails(null);
    setIsPackageDetailsValid(false);
  };

  const handlePackageChange = async (packageId: string) => {
    const requestId = (latestPackageRequestRef.current += 1);
    setIsDetailsLoading(true);
    setDetails(null);
    setIsPackageDetailsValid(false);
    try {
      const response = await fetchDetails(packageId);
      // A newer selection superseded this one — drop the stale response.
      if (requestId !== latestPackageRequestRef.current) return;
      if (response.Success) {
        setDetails(response.PackageDetails);
      } else {
        await showError(
          response.ErrorMessage ||
            t('FetchError.message', {
              defaultValue: 'An error occurred while fetching billing package details. Please try again.',
            }),
        );
      }
    } catch {
      if (requestId !== latestPackageRequestRef.current) return;
      await showError(
        t('FetchError.message', {
          defaultValue: 'An error occurred while fetching billing package details. Please try again.',
        }),
      );
    } finally {
      // Only the latest request owns the loading state.
      if (requestId === latestPackageRequestRef.current) setIsDetailsLoading(false);
    }
  };

  onPackageChangeRef.current = handlePackageChange;

  const packageDetailsRef = useRef<TabValuesMap>({});
  const billingPackageInfoRef = useRef<PackageDetailsType | null>(null);
  billingPackageInfoRef.current = details;

  useEffect(() => {
    onValuesChange({
      package: ctx.values.package,
      packageDetails: packageDetailsRef.current,
      billingPackageInfo: billingPackageInfoRef.current,
    });
  }, [ctx.values]);

  // Next stays blocked while sections still need reviewing; once all are reviewed, it stays
  // blocked while any section has validation errors. The two states share the same tooltip.
  const nextBlocker =
    reviewRemaining.length > 0
      ? {
          title: t('ReviewRemaining.title', { defaultValue: 'Please Review Remaining Steps' }),
          message: t('ReviewRemaining.message', {
            defaultValue: 'Please review: {{sections}}',
            sections: reviewRemaining.join(', '),
          }),
        }
      : errorSections.length > 0
        ? {
            title: t('ErrorsRemaining.title', { defaultValue: 'Errors Need Your Attention' }),
            message: t('ErrorsRemaining.message', {
              defaultValue: 'Please fix errors in: {{sections}}',
              sections: errorSections.join(', '),
            }),
          }
        : null;

  const tooltipContent = (
    <div className={cn('tooltipContent')}>
      <Title level="5">{t('Tooltip.title', { defaultValue: 'Important!' })}</Title>
      <p className={cn('tooltipDescription')}>
        {t('Tooltip.description', {
          defaultValue:
            'These services will appear on the Legal Agreement provided to the company. Please ensure vigilance while configuring them manually.',
        })}
      </p>
      <img
        src={agreementTooltipImg}
        alt={t('Tooltip.imageAlt', { defaultValue: 'Agreement PDF preview' })}
        className={cn('tooltipImage')}
      />
    </div>
  );

  return (
    <form className={cn('container')} onSubmit={ctx.handleSubmit}>
      <Header
        header={
          <div className={cn('header')}>
            {t('title', { defaultValue: 'Billing' })}
            <Tooltip
              information={tooltipContent}
              type="hover"
              variant="outlined"
              icon="info"
              position="rightTop"
              buttonWidth="18px"
              iconWidth="12"
              width="400px"
              informationClassName={cn('tooltipInformation')}
              hasArrow
            />
          </div>
        }
        description={t('description', {
          defaultValue: "Assign a billing package to the account & configure how they'll use VoPay Services.",
        })}
        rightArea={<StepIndicator steps={steps} activeStep={currentStep} />}
      />

      <div className={cn('leftCol')}>
        <DetailsCard className={cn('packageCard')}>
          {details === null && (
            <Title level="4">{t('Sections.PackageInfo.title', { defaultValue: 'Package Info' })}</Title>
          )}
          <div className={cn('packageGrid')}>{renderFormElements({ config: fields })}</div>
          {isDetailsLoading && (
            <>
              <div className={cn('skeletonRow')}>
                <Skeleton height={32} />
                <Skeleton height={32} />
              </div>
              <div className={cn('skeletonRow')}>
                <Skeleton height={32} />
                <Skeleton height={32} />
              </div>
            </>
          )}
        </DetailsCard>

        {isDetailsLoading && (
          <DetailsCard>
            <Skeleton height={22} width={138} />
            <div className={cn('skeletonRow')}>
              <Skeleton height={32} />
              <Skeleton height={32} />
            </div>
          </DetailsCard>
        )}

        {!isDetailsLoading && details !== null && (
          <PackageDetails
            details={details}
            onValidChange={setIsPackageDetailsValid}
            onReviewChange={setReviewRemaining}
            onErrorChange={setErrorSections}
            onValuesChange={(v) => {
              packageDetailsRef.current = v;
              onValuesChange({
                package: ctx.values.package,
                packageDetails: v,
                billingPackageInfo: billingPackageInfoRef.current,
              });
            }}
          />
        )}
      </div>

      <div className={cn('footer')}>
        <Button colorScheme="secondary" type="button" onClick={prevStep}>
          {t('back', { defaultValue: 'Back' })}
        </Button>
        {nextBlocker ? (
          <Tooltip
            type="hover"
            position="top"
            hasButton={false}
            width="280px"
            informationClassName={cn('reviewTooltipInfo')}
            customElement={
              <span className={cn('reviewNextWrap')}>
                <Button colorScheme="primary" disabled type="submit">
                  {t('next', { defaultValue: 'Next' })}
                </Button>
              </span>
            }
            information={
              <div className={cn('reviewTooltip')}>
                <Icon icon="cautionary2" size="xlg" width="40px" height="40px" color="none" />
                <Title level="6">{nextBlocker.title}</Title>
                <BodyText size="sm">{nextBlocker.message}</BodyText>
              </div>
            }
          />
        ) : (
          <Button
            colorScheme="primary"
            disabled={!ctx.isValid || isDetailsLoading || !isPackageDetailsValid}
            type="submit"
          >
            {t('next', { defaultValue: 'Next' })}
          </Button>
        )}
      </div>
    </form>
  );
};

export default Billing;
